import { useDeleteApi } from '@/apiError/apiDelete';
import { ShowAllData } from '@/apiError/apiShow';
import '@/assets/style/companyList.scss';
import InventorySubGroupForm from '@/components/forms/master/inventory/group/InventorySubGroupForm';
import CreateModalButton from '@/utils/components/CreateModelButton';
import DeleteModalButton from '@/utils/components/DeleteModalButton';
import EditModalButton from '@/utils/components/EditModalButton';
import SearchBox from '@/utils/components/SearchBox';
import GlobalFormModal from '@/utils/modal/GlobalFormModal';
import GlobalModal from '@/utils/modal/Modal';
import {
  fetchPermissions,
  hasPermission,
} from '@/utils/permissions/userPermission';
import { globalSearch } from '@/utils/search/Search';
import DynamicTable from '@/utils/table/DynamicTable';
import { inventoryGroupsTableHeader as baseColumns } from '@/utils/tableHeader';
import { Card, Col, Form, Row } from 'antd';
import { useEffect, useMemo, useState } from 'react';

/**
 * Group Component
 * Manages inventory groups, displaying a table with CRUD operations and search functionality.
 */
const SubGroup = () => {
  const [form] = Form.useForm();
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [groupData, setGroupData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTrem] = useState('');
  const [groupReferenceId, setGroupReferenceId] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isMessageModalVisible, setIsMessageModalVisible] = useState(false);
  const [selectedId, setSelectedId] = useState(null); // Holds data for the ledger to edit
  const [modalType, setModalType] = useState('');
  const [permissions, setPermissions] = useState([]);
  const { mutateAsync: deleteData, isPending: deleteLoading } = useDeleteApi();

  /**
   * Shows the delete confirmation modal for a specific group.
   * @param {string} referenceId - Reference ID of the group to delete
   */
  const showCancelModal = (referenceId) => {
    setModalType('delete');
    setIsMessageModalVisible(true);
    setGroupReferenceId(referenceId);
  };

  /**
   * Handles the closure of the modal.
   */
  const handleCancel = () => {
    setIsMessageModalVisible(false);
  };

  const openModal = (mode, id = null) => {
    setModalMode(mode);
    if (mode === 'edit' && id) {
      // fetchInitialEditData(id);
      setSelectedId(id);
    } else {
      setSelectedId(null);
    }
    setIsModalVisible(true);
  };

  const closeModal = () => {
    form.resetFields(); // Clears form data
    setSelectedId(null); // Clears selected item
    setIsModalVisible(false);
  };

  /**
   * Deletes the inventory group based on the modal type.
   * If modalType is 'delete', deletes the group; otherwise, closes the modal.
   */
  const deleteInventorySubGroup = async () => {
    if (!groupReferenceId) return;
    setIsMessageModalVisible(true);
    await deleteData(`account/inventory/sub-groups/${groupReferenceId}/`);
    setGroupData((prevData) =>
      prevData?.filter((group) => group.referenceId !== groupReferenceId),
    );
    setIsMessageModalVisible(false);
  };

  /**
   * Handles the change event of the search input.
   * @param {string} value - Search term entered by the user
   */
  const handleSearch = (value) => {
    setSearchTrem(value);
  };

  const filteredTableData = globalSearch(groupData, searchTerm);

  const FetchInventoryGroupList = async () => {
    try {
      setLoading(true);
      const response = await ShowAllData.showDetails(
        'account/inventory/sub-groups/',
      );
      setGroupData(response.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPermissions(fetchPermissions());
    FetchInventoryGroupList();
  }, []);

  const columns = useMemo(() => {
    const cols = [...baseColumns];

    if (
      hasPermission(permissions, 'Ledger', 'isUpdate') ||
      hasPermission(permissions, 'Ledger', 'isDelete')
    ) {
      cols.push({
        title: 'Action',
        key: 'action',
        align: 'center',
        width: '10%',
        fixed: 'right',

        render: (_, group) => (
          <div className="ant-action" style={{ display: 'flex' }}>
            {hasPermission(permissions, 'Inventory Group', 'isUpdate') && (
              <EditModalButton openModal={openModal} id={group?.key} />
            )}
            {hasPermission(permissions, 'Inventory Group', 'isDelete') && (
              <DeleteModalButton
                showCancelModal={showCancelModal}
                data={group?.key}
              />
            )}
          </div>
        ),
      });
    }

    return cols;
  }, [permissions]);

  return (
    <>
      <div
        style={{ position: 'relative', top: 0, marginTop: 0, paddingTop: 0 }}
      >
        <div className="table">
          <Row gutter={[24, 0]}>
            <Col xs={24} xl={24}>
              <Card
                variant="borderless"
                className="circlebox tablespace mb-24"
                title={
                  <>
                    {hasPermission(
                      permissions,
                      'Inventory Group',
                      'isCreate',
                    ) && (
                      <CreateModalButton
                        title="Create New Category"
                        openModal={openModal}
                      />
                    )}
                  </>
                }
                extra={
                  <SearchBox
                    searchPlaceholder="Search category..."
                    handleSearch={handleSearch}
                  />
                }
              >
                <div className="table-responsive">
                  <DynamicTable
                    data={filteredTableData}
                    columns={columns}
                    loading={loading}
                    scrollY
                  />
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
      <GlobalModal
        isVisible={isMessageModalVisible}
        type={modalType}
        onConfirm={deleteInventorySubGroup}
        onClose={handleCancel}
        loading={deleteLoading}
      />
      <GlobalFormModal
        isVisible={isModalVisible}
        title={
          modalMode === 'create'
            ? 'Create Inventory Category'
            : 'Update Inventory Category'
        }
        onClose={closeModal}
        loading={false}
        isCustomWidth={'30vw'}
      >
        <InventorySubGroupForm
          referenceId={selectedId}
          onClose={closeModal}
          refreshTable={FetchInventoryGroupList}
          form={form}
        />
      </GlobalFormModal>
    </>
  );
};

export default SubGroup;
